
import { DataEngineeringCourse } from './types';

export const DATAENGINEERING_FUNDAMENTALS_COURSE: DataEngineeringCourse = {
  id: 'dataengineering-fundamentals',
  title: 'Data Engineering Fundamentals',
  description: 'Master the essentials of data engineering, from SQL and Python to ETL pipelines and cloud platforms.',
  level: 'fundamentals',
  totalDuration: '14-16 hours',
  image: '/api/placeholder/400/300',
  prerequisites: [
    'Basic programming knowledge',
    'Familiarity with databases',
    'Understanding of command line basics'
  ],
  learningOutcomes: [
    'Understand data engineering role and responsibilities',
    'Master SQL for data manipulation and analysis',
    'Build ETL pipelines using Python',
    'Implement data quality and validation processes',
    'Work with various data storage systems',
    'Deploy basic cloud data solutions',
    'Apply version control for data projects',
    'Monitor and maintain data pipelines'
  ],
  modules: [
    {
      id: 'introduction-to-data-engineering',
      title: 'Introduction to Data Engineering',
      description: 'Understanding the role, responsibilities, and career paths in data engineering.',
      content: `# Introduction to Data Engineering

## What is Data Engineering?

Data engineering is the practice of designing and building systems for collecting, storing, and analyzing data at scale. Data engineers create the infrastructure and tools that enable data scientists and analysts to work effectively.

### Key Responsibilities

1. **Data Pipeline Development**
   - Extract, Transform, Load (ETL) processes
   - Real-time streaming data processing
   - Batch processing workflows

2. **Data Infrastructure**
   - Database design and optimization
   - Cloud platform management
   - Scalability and performance tuning

3. **Data Quality & Governance**
   - Data validation and testing
   - Monitoring and alerting
   - Compliance and security

### Career Paths

- **Junior Data Engineer**: Focus on learning tools and building simple pipelines
- **Senior Data Engineer**: Design complex systems and mentor junior engineers
- **Principal Data Engineer**: Architecture decisions and technical leadership
- **Data Engineering Manager**: Team leadership and strategic planning

### Modern Data Stack

The modern data engineering ecosystem includes:
- **Cloud Platforms**: AWS, Azure, GCP
- **Orchestration**: Apache Airflow, Prefect
- **Processing**: Apache Spark, dbt
- **Storage**: Data lakes, warehouses, lakehouses
- **Streaming**: Apache Kafka, Amazon Kinesis`,
      duration: '2 hours',
      difficulty: 'beginner',
      objectives: [
        'Define data engineering and its role in organizations',
        'Identify key responsibilities and skills needed',
        'Understand career progression paths',
        'Overview of modern data engineering tools'
      ],
      codeExamples: [
        {
          title: 'Simple Data Pipeline Structure',
          language: 'python',
          code: `import pandas as pd
from datetime import datetime

def extract_data(source_path):
    """Extract data from source"""
    return pd.read_csv(source_path)

def transform_data(df):
    """Transform and clean data"""
    df['processed_at'] = datetime.now()
    df = df.dropna()
    return df

def load_data(df, destination_path):
    """Load data to destination"""
    df.to_csv(destination_path, index=False)
    print(f"Loaded {len(df)} records to {destination_path}")

# Simple ETL pipeline
def run_pipeline(source, destination):
    data = extract_data(source)
    cleaned_data = transform_data(data)
    load_data(cleaned_data, destination)`,
          explanation: 'A basic ETL pipeline structure showing the three main phases of data processing.'
        }
      ],
      quiz: {
        questions: [
          {
            id: 'de-intro-1',
            question: 'What is the primary role of a data engineer?',
            options: [
              'Analyzing data to find business insights',
              'Building and maintaining data infrastructure',
              'Creating machine learning models',
              'Designing user interfaces'
            ],
            correctAnswer: 1,
            explanation: 'Data engineers focus on building and maintaining the infrastructure that enables data collection, storage, and processing.'
          },
          {
            id: 'de-intro-2',
            question: 'Which of the following is NOT typically a data engineering responsibility?',
            options: [
              'Building ETL pipelines',
              'Data quality monitoring',
              'Statistical analysis and reporting',
              'Database optimization'
            ],
            correctAnswer: 2,
            explanation: 'Statistical analysis and reporting is typically the responsibility of data analysts or data scientists.'
          }
        ]
      }
    },
    {
      id: 'database-fundamentals',
      title: 'Database Fundamentals',
      description: 'Master SQL and database concepts essential for data engineering.',
      content: `# Database Fundamentals

## SQL Mastery for Data Engineers

SQL is the foundation of data engineering. Beyond basic queries, data engineers need advanced SQL skills for complex data transformations.

### Advanced SQL Concepts

1. **Window Functions**
   - ROW_NUMBER(), RANK(), DENSE_RANK()
   - LAG(), LEAD() for time series analysis
   - Cumulative aggregations

2. **Common Table Expressions (CTEs)**
   - Recursive CTEs for hierarchical data
   - Multiple CTEs for complex logic
   - Performance considerations

3. **Performance Optimization**
   - Index strategies
   - Query execution plans
   - Partitioning techniques

### ACID Properties

- **Atomicity**: All operations in a transaction succeed or fail together
- **Consistency**: Database remains in valid state after transactions
- **Isolation**: Concurrent transactions don't interfere with each other
- **Durability**: Committed transactions are permanently stored

### Database Types

1. **Relational (OLTP)**
   - PostgreSQL, MySQL, SQL Server
   - ACID compliance, normalized data
   - Transaction processing

2. **Analytical (OLAP)**
   - Snowflake, BigQuery, Redshift
   - Columnar storage, denormalized
   - Analytical workloads

3. **NoSQL**
   - Document: MongoDB, CosmosDB
   - Key-Value: Redis, DynamoDB
   - Graph: Neo4j, Amazon Neptune`,
      duration: '2 hours',
      difficulty: 'beginner',
      objectives: [
        'Write advanced SQL queries with window functions',
        'Understand ACID properties and their importance',
        'Compare different database types and use cases',
        'Optimize query performance'
      ],
      codeExamples: [
        {
          title: 'Advanced SQL with Window Functions',
          language: 'sql',
          code: `-- Calculate running totals and rankings
WITH sales_analysis AS (
  SELECT 
    date,
    product_id,
    sales_amount,
    -- Running total
    SUM(sales_amount) OVER (
      PARTITION BY product_id 
      ORDER BY date 
      ROWS UNBOUNDED PRECEDING
    ) as running_total,
    -- Previous day comparison
    LAG(sales_amount) OVER (
      PARTITION BY product_id 
      ORDER BY date
    ) as previous_day_sales,
    -- Ranking within product
    ROW_NUMBER() OVER (
      PARTITION BY product_id 
      ORDER BY sales_amount DESC
    ) as sales_rank
  FROM daily_sales
)
SELECT 
  *,
  sales_amount - COALESCE(previous_day_sales, 0) as daily_change
FROM sales_analysis
WHERE sales_rank <= 10;`,
          explanation: 'Advanced SQL demonstrating window functions for analytics common in data engineering.'
        }
      ]
    },
    {
      id: 'python-for-data-engineering',
      title: 'Python for Data Engineering',
      description: 'Learn Python libraries and patterns specific to data engineering tasks.',
      content: `# Python for Data Engineering

## Essential Libraries

### Pandas for Data Manipulation
Pandas is crucial for data transformation and analysis in data pipelines.

### SQLAlchemy for Database Connections
Object-relational mapping and database abstraction layer.

### Apache Airflow for Orchestration
Python-based workflow orchestration platform.

## Data Engineering Patterns

1. **Configuration Management**
   - Environment variables
   - Configuration files
   - Secrets management

2. **Error Handling**
   - Robust exception handling
   - Retry mechanisms
   - Dead letter queues

3. **Logging and Monitoring**
   - Structured logging
   - Metrics collection
   - Alerting systems

## File Processing

Data engineers work with various file formats:
- **CSV**: Simple tabular data
- **JSON**: Semi-structured data
- **Parquet**: Columnar format for analytics
- **Avro**: Schema evolution support`,
      duration: '2 hours',
      difficulty: 'beginner',
      objectives: [
        'Use Pandas for complex data transformations',
        'Connect to databases using SQLAlchemy',
        'Handle various file formats efficiently',
        'Implement proper error handling and logging'
      ],
      codeExamples: [
        {
          title: 'Database Connection and Data Processing',
          language: 'python',
          code: `import pandas as pd
from sqlalchemy import create_engine
import logging
from typing import Optional

# Setup logging
logging.basicConfig(level=logging.INFO)
logger = logging.getLogger(__name__)

class DataProcessor:
    def __init__(self, connection_string: str):
        self.engine = create_engine(connection_string)
    
    def extract_data(self, query: str) -> Optional[pd.DataFrame]:
        """Extract data from database with error handling"""
        try:
            df = pd.read_sql(query, self.engine)
            logger.info(f"Extracted {len(df)} rows")
            return df
        except Exception as e:
            logger.error(f"Error extracting data: {e}")
            return None
    
    def transform_data(self, df: pd.DataFrame) -> pd.DataFrame:
        """Apply transformations with validation"""
        if df is None:
            raise ValueError("Input dataframe is None")
        
        # Data cleaning
        df_clean = df.dropna()
        
        # Type conversions
        if 'date' in df_clean.columns:
            df_clean['date'] = pd.to_datetime(df_clean['date'])
        
        # Add metadata
        df_clean['processed_timestamp'] = pd.Timestamp.now()
        
        logger.info(f"Transformed data: {len(df_clean)} rows")
        return df_clean
    
    def load_data(self, df: pd.DataFrame, table_name: str):
        """Load data to database"""
        try:
            df.to_sql(table_name, self.engine, 
                     if_exists='append', index=False)
            logger.info(f"Loaded {len(df)} rows to {table_name}")
        except Exception as e:
            logger.error(f"Error loading data: {e}")
            raise`,
          explanation: 'A robust data processing class with proper error handling and logging for production use.'
        }
      ]
    },
    // ... additional modules would continue here
  ]
};
